import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';
import {
  getLoSiteHeroMedia,
  getLoSiteHeroVideoPresentation,
  heroBackgroundPositionCss,
  heroHasBackgroundMedia,
} from '@/lib/lyrraone-site-hero-media';
import { resolveSiteImageUrl } from '@/components/common/ImageSourcePicker';

interface LoSiteHeroBackgroundProps {
  block: LoSiteBlock;
}

export function LoSiteHeroBackground({ block }: LoSiteHeroBackgroundProps) {
  const media = getLoSiteHeroMedia(block);
  if (!heroHasBackgroundMedia(block)) return null;

  const overlayOpacity = Math.min(80, Math.max(0, media.overlay)) / 100;

  if (media.type === 'image' && media.imageUrl.trim()) {
    const src = resolveSiteImageUrl(media.imageUrl);
    return (
      <>
        <div
          className="lo-st-hero__bg lo-st-hero__bg--image"
          style={{
            backgroundImage: `url("${src}")`,
            backgroundSize: media.size,
            backgroundPosition: heroBackgroundPositionCss(media.position),
          }}
          aria-hidden
        />
        <div className="lo-st-hero__bg-overlay" style={{ opacity: overlayOpacity }} aria-hidden />
      </>
    );
  }

  if (media.type === 'video' && media.videoUrl.trim()) {
    const presentation = getLoSiteHeroVideoPresentation(media.videoUrl, {
      muted: media.videoMuted,
      loop: media.videoLoop,
    });
    if (!presentation) return null;

    if (presentation.mode === 'youtube' || presentation.mode === 'vimeo') {
      return (
        <>
          <div className="lo-st-hero__bg lo-st-hero__bg--embed" aria-hidden>
            <iframe
              src={presentation.embedSrc}
              title=""
              className="lo-st-hero__bg-embed"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              tabIndex={-1}
            />
          </div>
          <div className="lo-st-hero__bg-overlay" style={{ opacity: overlayOpacity }} aria-hidden />
        </>
      );
    }

    return (
      <>
        <video
          className="lo-st-hero__bg lo-st-hero__bg--video"
          src={presentation.src}
          autoPlay
          muted={media.videoMuted}
          loop={media.videoLoop}
          playsInline
          aria-hidden
        />
        <div className="lo-st-hero__bg-overlay" style={{ opacity: overlayOpacity }} aria-hidden />
      </>
    );
  }

  return null;
}
